/**
 * Item page → Connections → "Linked systems" (owner ruling 2026-10-15).
 *
 * Each outside system this item is linked to — its key there (a link out when
 * one is recorded), when it last synced, and what the connector's field
 * mappings carry: which of its fields come in to which of ours, and which go
 * out. The mappings themselves are kept in Settings → Field Mappings; this
 * only shows them where the item is.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2, RefreshCw } from 'lucide-react';
import { getConnectorDefinition, type ConnectorType } from '@/lib/connectors';
import { loadFieldMappingConfig, type FieldMappingSet } from '@/lib/fieldMappingRegistry';
import { externalFieldLabel, internalFieldLabel, resolveActiveMappingSet, ruleRunsOn } from '@/lib/fieldMappingApply';
import { describeLinearSync, syncLinkedLinearIssue } from '@/lib/linearItemSync';
import { describeJiraSync, syncLinkedJiraIssue } from '@/lib/jiraItemSync';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { externalLinkDisplayKey, type ItemExternalLink } from '@/hooks/prioritization/useItemExternalLinks';

const FIELD_MAPPINGS_PATH = '/prioritization/settings/field-mappings';

interface Carried {
  setName: string | null;
  comesIn: string[];
  goesOut: string[];
}

function connectorName(type: string): string {
  return getConnectorDefinition(type as ConnectorType)?.name ?? type.charAt(0).toUpperCase() + type.slice(1);
}

function carriedBy(set: FieldMappingSet | null, type: string, flow: 'import' | 'export', label: (k: string) => string | undefined): string[] {
  if (!set) return [];
  return set.entries
    .filter((e) => ruleRunsOn(e, flow))
    .map((e) =>
      flow === 'import'
        ? `${externalFieldLabel(type, e)} → ${internalFieldLabel(e.internalFieldKey, label)}`
        : `${internalFieldLabel(e.internalFieldKey, label)} → ${externalFieldLabel(type, e)}`,
    );
}

export function ItemLinkedSystemsSection({
  workspaceId,
  itemId,
  links,
  externalId,
  onLink,
}: {
  workspaceId: string | null;
  /** The item the links belong to — Sync now needs it. */
  itemId?: string;
  links: ItemExternalLink[] | null;
  /** items.external_id, when an import recorded one without a link row. */
  externalId?: string | null;
  /** Opens the item's link-and-sync panels. */
  onLink?: () => void;
}) {
  const attributes = useAttributesStore((s) => s.attributes);
  const mayChangeMappings = useUserStore((s) => s.can('admin.settings'));
  const mayEdit = useUserStore((s) => s.can('items.edit'));
  const userId = useUserStore((s) => s.userId);
  const [carried, setCarried] = useState<Record<string, Carried>>({});
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncNote, setSyncNote] = useState<{ key: string; text: string } | null>(null);

  const syncNow = async (l: ItemExternalLink) => {
    if (!workspaceId || !itemId) return;
    setSyncing(l.externalId);
    setSyncNote(null);
    try {
      if (l.connectorType === 'jira') {
        const result = await syncLinkedJiraIssue({ workspaceId, itemId, issueKey: l.externalId, issueUrl: l.externalUrl, userId, reason: 'now' });
        setSyncNote({ key: l.externalId, text: describeJiraSync(result) });
      } else {
        const result = await syncLinkedLinearIssue({ workspaceId, itemId, issueId: l.externalId, userId, reason: 'now' });
        setSyncNote({ key: l.externalId, text: describeLinearSync(result) });
      }
    } finally {
      setSyncing(null);
    }
  };

  const types = [...new Set((links ?? []).map((l) => l.connectorType))];
  const typesKey = types.join(',');

  useEffect(() => {
    if (!workspaceId || !typesKey) return;
    let live = true;
    const label = (k: string) => attributes.find((a) => a.key === k)?.label;
    Promise.all(
      typesKey.split(',').map(async (type) => {
        const config = await loadFieldMappingConfig(workspaceId, type).catch(() => null);
        const inSet = config ? resolveActiveMappingSet(config, 'import').set : null;
        const outSet = config ? resolveActiveMappingSet(config, 'export').set : null;
        return [
          type,
          {
            setName: inSet?.name ?? outSet?.name ?? null,
            comesIn: carriedBy(inSet, type, 'import', label),
            goesOut: carriedBy(outSet, type, 'export', label),
          },
        ] as const;
      }),
    ).then((rows) => live && setCarried(Object.fromEntries(rows)));
    return () => {
      live = false;
    };
  }, [workspaceId, typesKey, attributes]);

  const loose = externalId && !(links ?? []).some((l) => l.externalId === externalId) ? externalId : null;

  return (
    <div id="item-linked-systems" className="scroll-mt-24 space-y-3" data-testid="item-linked-systems">
      {links === null ? (
        <p className="text-sm text-[var(--text-ter)]">Reading links…</p>
      ) : links.length === 0 && !loose ? (
        <p className="text-sm text-[var(--text-ter)]">
          Not linked to any outside system.
          {onLink && (
            <>
              {' '}
              <button type="button" className="underline hover:text-[var(--text-pri)]" onClick={onLink}>
                Link or sync
              </button>
            </>
          )}
        </p>
      ) : (
        <>
          {links.map((l) => {
            const c = carried[l.connectorType];
            return (
              <div key={`${l.connectorType}:${l.externalId}`} className="border border-[var(--rule)] px-3 py-2.5">
                <p className="text-sm text-[var(--text-sec)]">
                  <strong className="text-[var(--text-pri)]">{connectorName(l.connectorType)}</strong>{' '}
                  {l.externalUrl ? (
                    <a href={l.externalUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono underline">
                      {externalLinkDisplayKey(l)}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="font-mono">{externalLinkDisplayKey(l)}</span>
                  )}
                  <span className="text-[var(--text-ter)]">
                    {' · '}
                    {l.lastSyncedAt ? `synced ${new Date(l.lastSyncedAt).toLocaleString()}` : 'not synced yet'}
                  </span>
                  {(l.connectorType === 'linear' || l.connectorType === 'jira') && mayEdit && itemId && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        onClick={() => void syncNow(l)}
                        disabled={syncing !== null}
                        className="inline-flex items-center gap-1 text-xs underline hover:text-[var(--text-pri)] disabled:opacity-50"
                        data-testid="linked-systems-sync-now"
                      >
                        {syncing === l.externalId ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                        Sync now
                      </button>
                    </>
                  )}
                </p>
                {syncNote?.key === l.externalId && (
                  <p className="mt-1 text-xs text-[var(--text-sec)]" data-testid="linked-systems-sync-note">{syncNote.text}</p>
                )}
                {c && (
                  <div className="mt-1 space-y-0.5 text-xs text-[var(--text-ter)]">
                    {c.setName === null ? (
                      <p>No field mapping set for {connectorName(l.connectorType)}, so no fields are copied either way.</p>
                    ) : (
                      <>
                        <p>Mapping set “{c.setName}”.</p>
                        <p>Comes in: {c.comesIn.length ? c.comesIn.join('; ') : 'nothing'}.</p>
                        <p>Goes out: {c.goesOut.length ? c.goesOut.join('; ') : 'nothing'}.</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {loose && (
            <p className="text-sm text-[var(--text-sec)]">
              Outside ID <span className="font-mono">{loose}</span>
              <span className="text-[var(--text-ter)]"> · recorded on import, no link to follow</span>
            </p>
          )}
          <p className="text-xs text-[var(--text-ter)]">
            {onLink && (
              <>
                <button type="button" className="underline hover:text-[var(--text-pri)]" onClick={onLink}>
                  Link or sync
                </button>
                {mayChangeMappings ? ' · ' : ''}
              </>
            )}
            {mayChangeMappings && (
              <Link to={FIELD_MAPPINGS_PATH} className="underline hover:text-[var(--text-pri)]">
                Change what is copied in Settings → Field Mappings
              </Link>
            )}
          </p>
        </>
      )}
    </div>
  );
}
